// Third Party Imports
import React, {Fragment, PureComponent} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {CHEVRON_LEFT__24, CHEVRON_RIGHT__24} from '@homeaway/svg-defs';
import {SVGIcon} from '@homeaway/react-svg';
// Primary Imports
import util from '../../utils/util';

// Style
import './SingleImageCarousel.less';

class SingleImageCarousel extends PureComponent {
    static propTypes = {
        /** Background-size treatment of images */
        backgroundSize: PropTypes.oneOf(['cover', 'contain']),
        /** className to pass through to root element of this component */
        className: PropTypes.string,
        /** The percentage changed threshold on swipe required to change the image. */
        changeThreshold: PropTypes.number,
        /** The index of the image to show. */
        index: PropTypes.number.isRequired,
        /** The array of image data objects, with 'src' and 'altText' properties */
        images: PropTypes.arrayOf(
            PropTypes.shape({
                src: PropTypes.string.isRequired,
                altText: PropTypes.string
            })
        ).isRequired,
        /** The transition duration (in milliseconds) when moving from one image index to the next or previous. Transistion occurs after click or pan. */
        transitionDuration: PropTypes.number,
        /** The transition style when moving form one image to the next. Swipe interaction only supports "slide". */
        transitionStyle: PropTypes.oneOf(['slide', 'slideOver', 'fade']),
        /** The tab index for carousel */
        tabIndex: PropTypes.number,
        /** The click handler for the entire carousel */
        onClick: PropTypes.func,
        /** The previous button click handler callback */
        onPrevClick: PropTypes.func,
        /** The next button click handler callback */
        onNextClick: PropTypes.func,
        /** A render method that returns the next and previous buttons. These buttons are rendered in an absolutely positioned container element that is the size of the entire carousel viewport. This method takes {setPrevRef, setNextRef, prevLabel, nextLabel} as an argument and requires the correct implementation to assign the ref callback and label in the returned elements. Please use the default implementation as a guide.*/
        renderButtons: PropTypes.func,
        /** The URL to navigate to when carousel (not including nav buttons) is clicked. This URL navigation will occur after the click handler. */
        href: PropTypes.string,
        /** Where to display the linked URL, as the name for a browsing context, if specified. Implicitly defaults to "self" */
        target: PropTypes.oneOf(['_blank', '_self', '_parent', '_top']),
        /** The text labels for ui (localization) and screen readers */
        labels: PropTypes.shape({
            anchorLink: PropTypes.string,
            nextButton: PropTypes.string,
            prevButton: PropTypes.string,
            imageStatus: PropTypes.string
        }),
        /** A handler that returns the new index as the transition starts, useful for syncing animations w/ consuming app   */
        onIndexChangeStart: PropTypes.func
    };

    static defaultProps = {
        backgroundSize: 'cover',
        changeThreshold: 20,
        index: 0,
        images: [],
        transitionDuration: 250,
        transitionStyle: 'slide',
        tabIndex: 0,
        labels: {
            anchorLink: 'View property',
            nextButton: 'Next image',
            prevButton: 'Previous image',
            ariaLiveStatus: 'Image {current} of {total}'
        },
        renderButtons: ({setPrevRef, setNextRef, prevLabel, nextLabel}) => {
            return (
                <Fragment>
                    <button
                        key="0"
                        className="SingleImageCarousel__button SingleImageCarousel__button--left"
                        data-wdio="SingleImageCarousel__button--prev"
                        ref={setPrevRef}
                        aria-label={prevLabel}
                    >
                        <SVGIcon
                            className="SingleImageCarousel__svg SingleImageCarousel__svg--left"
                            markup={CHEVRON_LEFT__24}
                        />
                    </button>
                    <button
                        key="1"
                        className="SingleImageCarousel__button SingleImageCarousel__button--right"
                        data-wdio="SingleImageCarousel__button--next"
                        ref={setNextRef}
                        aria-label={nextLabel}
                    >
                        <SVGIcon
                            className="SingleImageCarousel__svg SingleImageCarousel__svg--right"
                            markup={CHEVRON_RIGHT__24}
                        />
                    </button>
                </Fragment>
            );
        },
        onIndexChangeStart: null
    };

    constructor(props) {
        super(props);
        const {images, index} = this.props;
        this.setMainRef = this.setRef.bind(this, 'main');
        this.setPrevRef = this.setRef.bind(this, 'prev');
        this.setNextRef = this.setRef.bind(this, 'next');
        this.onPrev = this.onChangeIndex.bind(this, 'prev');
        this.onNext = this.onChangeIndex.bind(this, 'next');
        this.state = {
            backgroundSrc: util.getImageSrc(images[index]),
            preloadSrc: '',
            transitionDirection: '',
            index,
            panPct: 0
        };
        this.keyUp = true;
        this.timeouts = {};
        this.x = null;
        this.y = null;
        this.panAxis = null;
        this.touchMoved = false;
    }

    componentWillUnmount() {
        const {onClick} = this.props;
        /* istanbul ignore else */
        if (this.main) {
            this.main.removeEventListener('click', onClick);
            this.main.removeEventListener('keydown', this.onKeyDown);
            this.main.removeEventListener('keyup', this.onKeyUp);
            this.main.removeEventListener('touchmove', this.handleTouchMove);
            this.main.removeEventListener('touchend', this.handleTouchEnd);
        }
        /* istanbul ignore else */
        if (this.prev) {
            this.prev.removeEventListener('click', this.onPrev);
        }
        /* istanbul ignore else */
        if (this.next) {
            this.next.removeEventListener('click', this.onNext);
        }
    }

    transition(dir) {
        const {transitionDuration, images, index} = this.props;
        const reset = dir === 'reset';

        const updatedIndex = reset
            ? index
            : util.getUpdatedIndex(dir, index, images.length);
        const preloadIndex = reset
            ? index
            : util.getPreloadIndex(dir, index, images.length);

        this.setState({
            transitionDirection: dir,
            backgroundSrc: util.getImageSrc(images[updatedIndex]),
            preloadSrc: util.getImageSrc(images[preloadIndex]),
            panPct: 0
        });
        clearTimeout(this.timeouts.slide);
        this.timeouts.slide = setTimeout(() => {
            clearTimeout(this.timeouts.slide);
            this.setState({transitionDirection: ''});
            if (!reset) {
                /* istanbul ignore else */
                if (dir === 'next') {
                    if (this.props.onNextClick) {
                        this.props.onNextClick(updatedIndex);
                    }
                } else if (this.props.onPrevClick) {
                    this.props.onPrevClick(updatedIndex);
                }
            }
        }, transitionDuration);
    }

    onKeyDown = (e) => {
        const {key, target} = e;
        const {onClick} = this.props;
        if (
            this.keyUp &&
            target === this.main &&
            (key === 'Enter' || key === ' ')
        ) {
            this.keyUp = false;
            if (key === ' ') {
                e.preventDefault();
            }
            /* istanbul ignore else */
            if (onClick) {
                onClick();
            }
        }
    };

    onKeyUp = (e) => {
        const {key, target} = e;
        if (
            !this.keyUp &&
            target === this.main &&
            (key === 'Enter' || key === ' ')
        ) {
            this.keyUp = true;
        }
    };

    handleTouchEnd = (e) => {
        const {changeThreshold} = this.props;
        const {panPct} = this.state;
        this.x = null;
        this.y = null;
        this.panAxis = null;
        if (this.touchMoved) {
            if (panPct <= -changeThreshold) {
                this.onNext(e);
            } else if (panPct >= changeThreshold) {
                this.onPrev(e);
            } else {
                this.transition('reset');
            }
        }
        this.touchMoved = false;
    };

    handleTouchMove = (e) => {
        const clientX = e.touches[0].clientX;
        const clientY = e.touches[0].clientY;

        if (this.x && this.y) {
            const xDiff = this.x - clientX;
            this.touchMoved = true;
            // determine pan axis for this swipe
            /* istanbul ignore else */
            if (!this.panAxis) {
                const yDiffAbs = Math.abs(this.y - clientY);
                this.panAxis = Math.abs(xDiff) > yDiffAbs ? 'horz' : 'vert';
            }
            /* Get the start position and direction */
            if (this.panAxis === 'horz') {
                /* Prevent vertical scrolling while are are swiping on the image */
                e.preventDefault();
                const width = e.currentTarget.clientWidth;
                const panPct = Math.round(-(xDiff / width) * 100);
                this.setState({
                    panPct: Math.min(Math.max(-100, panPct), 100)
                });
            }
        } else {
            // essentially touch start
            this.x = clientX;
            this.y = clientY;
        }
    };

    onChangeIndex(dir, e) {
        e.stopPropagation();
        e.preventDefault();
        if (this.props.onIndexChangeStart) {
            const {images, index} = this.props;
            const reset = dir === 'reset';
            const updatedIndex = reset
                ? index
                : util.getUpdatedIndex(dir, index, images.length);
            this.props.onIndexChangeStart(updatedIndex);
        }
        this.transition(dir);
    }

    setRef(name, ref) {
        const {onClick} = this.props;
        const methodsMap = {
            prev: this.onPrev,
            next: this.onNext,
            main: onClick
        };
        this[name] = ref;
        /* istanbul ignore else */
        if (ref && ref.addEventListener) {
            if (methodsMap[name]) {
                ref.addEventListener('click', methodsMap[name]);
            }
            if (name === 'main') {
                ref.addEventListener('keydown', this.onKeyDown);
                ref.addEventListener('keyup', this.onKeyUp);
                ref.addEventListener('touchmove', this.handleTouchMove);
                ref.addEventListener('touchend', this.handleTouchEnd);
            }
        }
    }

    render() {
        const {
            transitionDirection,
            preloadSrc,
            backgroundSrc,
            panPct
        } = this.state;
        const {
            index,
            images,
            transitionStyle,
            transitionDuration,
            className,
            tabIndex,
            href,
            target,
            labels,
            backgroundSize,
            renderButtons
        } = this.props;
        const nextIndex = util.getNextIndex(index, images.length);
        const prevIndex = util.getPrevIndex(index, images.length);
        const rootClass = classnames('SingleImageCarousel', className, {
            'SingleImageCarousel--transitioning': !!transitionDirection,
            'SingleImageCarousel--cover': backgroundSize === 'cover',
            'SingleImageCarousel--contain': backgroundSize === 'contain'
        });
        const rootStyle = util.appendBackgroundStyle({}, backgroundSrc);
        const currentClass = classnames(
            'SingleImageCarousel__image SingleImageCarousel__image--current',
            {
                'SingleImageCarousel__image--transitioning-prev-slide':
                    transitionDirection === 'prev' &&
                    transitionStyle === 'slide',
                'SingleImageCarousel__image--transitioning-next-slide':
                    transitionDirection === 'next' &&
                    transitionStyle === 'slide'
            }
        );
        const currentStyle = util.getImageStyle({
            transitionDirection,
            image: images[index],
            imageDirection: 'either',
            transitionDuration,
            transitionStyle,
            panPct,
            slideType: 'current'
        });
        const nextClass = classnames('SingleImageCarousel__image', {
            'SingleImageCarousel__image--next-slide':
                transitionStyle === 'slide' || transitionStyle === 'slideOver',
            'SingleImageCarousel__image--next-fade': transitionStyle === 'fade',
            'SingleImageCarousel__image--transitioning-slide':
                transitionDirection === 'next' &&
                (transitionStyle === 'slide' ||
                    transitionStyle === 'slideOver'),
            'SingleImageCarousel__image--transitioning-fade':
                transitionDirection === 'next' && transitionStyle === 'fade'
        });
        const nextStyle = util.getImageStyle({
            transitionDirection,
            image: images[nextIndex],
            imageDirection: 'next',
            transitionDuration,
            transitionStyle,
            panPct,
            slideType: 'next'
        });
        const prevClass = classnames('SingleImageCarousel__image', {
            'SingleImageCarousel__image--prev-slide':
                transitionStyle === 'slide' || transitionStyle === 'slideOver',
            'SingleImageCarousel__image--prev-fade': transitionStyle === 'fade',
            'SingleImageCarousel__image--transitioning-slide':
                transitionDirection === 'prev' &&
                (transitionStyle === 'slide' ||
                    transitionStyle === 'slideOver'),
            'SingleImageCarousel__image--transitioning-fade':
                transitionDirection === 'prev' && transitionStyle === 'fade'
        });
        const prevStyle = util.getImageStyle({
            transitionDirection,
            image: images[prevIndex],
            imageDirection: 'prev',
            transitionDuration,
            transitionStyle,
            panPct,
            slideType: 'prev'
        });
        const preloadStyle = util.appendBackgroundStyle({}, preloadSrc);
        return (
            <a
                aria-label={labels.anchorLink}
                className={rootClass}
                data-imgsrcurl={backgroundSrc}
                data-index={index}
                data-wdio="SingleImageCarousel--main"
                ref={this.setMainRef}
                role="button"
                style={rootStyle}
                tabIndex={tabIndex}
                target={target}
                href={href}
            >
                {images.length > 1 && (
                    <div>
                        <div
                            className="SingleImageCarousel__live-region sr-only"
                            aria-live="polite"
                            aria-atomic="true"
                        >
                            {util.getTemplateText(labels.ariaLiveStatus, {
                                current: index + 1,
                                total: images.length
                            })}
                        </div>
                        <ul className="SingleImageCarousel__images">
                            <li
                                className={currentClass}
                                style={currentStyle}
                                aria-label={util.getAltText(images[index])}
                            />
                            <li
                                className={nextClass}
                                style={nextStyle}
                                aria-hidden="true"
                            />
                            <li
                                className={prevClass}
                                style={prevStyle}
                                aria-hidden="true"
                            />
                            <li
                                className="SingleImageCarousel__preloader"
                                style={preloadStyle}
                                aria-hidden="true"
                            />
                        </ul>
                        {renderButtons({
                            setPrevRef: this.setPrevRef,
                            setNextRef: this.setNextRef,
                            nextLabel: labels.nextButton,
                            prevLabel: labels.prevButton
                        })}
                    </div>
                )}
            </a>
        );
    }
}

export default SingleImageCarousel;
